import {ValidationError} from './../ducks/independentAgent';
import {Email, ValidEmail} from 'Core/models/Email';
import {NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgencyAssociation, ValidAgencyAssociation} from 'Users/models/AgencyAssociation';
import {ValidAgentNumber} from 'Users/models/AgentNumber';

export class UnvalidatedIndependentAgentRegistration {
  public readonly key = 'UnvalidatedIndependentAgentRegistration';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly email: Email,
    public readonly agencyAssociations: AgencyAssociation[],
    public readonly agentNumbers: ValidAgentNumber[]
  ) {}
}

export class InvalidIndependentAgentRegistration {
  public readonly key = 'InvalidIndependentAgentRegistration';
  public readonly validationError = '.';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly email: Email,
    public readonly agencyAssociations: AgencyAssociation[],
    public readonly agentNumbers: ValidAgentNumber[]
  ) {}
}

export class ValidIndependentAgentRegistration {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    email: Email,
    agencyAssociations: AgencyAssociation[],
    agentNumbers: ValidAgentNumber[]
  ) {
    return firstName instanceof NonEmptyString &&
      lastName instanceof NonEmptyString &&
      email instanceof ValidEmail &&
      ((agencyAssociations.length > 0 &&
        agencyAssociations.every(association => association instanceof ValidAgencyAssociation)) ||
        agentNumbers.length > 0) 
      ? new ValidIndependentAgentRegistration(firstName, lastName, email, agencyAssociations, agentNumbers)
      : new InvalidIndependentAgentRegistration(firstName, lastName, email, agencyAssociations, agentNumbers);
  }

  public readonly key = 'ValidIndependentAgentRegistration';
  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly email: ValidEmail,
    public readonly agencyAssociations: AgencyAssociation[],
    public readonly agentNumbers: ValidAgentNumber[]
  ) {}
}

export class IndependentAgentRegistrationWithError {
  public static create(registration: ValidIndependentAgentRegistration, errorMessage: string) {
    return new IndependentAgentRegistrationWithError(
      registration.firstName,
      registration.lastName,
      registration.email,
      registration.agencyAssociations,
      registration.agentNumbers,
      errorMessage
    );
  }
  public readonly key = 'IndependentAgentRegistrationWithError';

  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly email: ValidEmail,
    public readonly agencyAssociations: AgencyAssociation[],
    public readonly agentNumbers: ValidAgentNumber[],
    public readonly errorMessage: string
  ) {}
}

export class SavingIndependentAgentRegistration {
  public static create(registration: ValidIndependentAgentRegistration) {
    return new SavingIndependentAgentRegistration(
      registration.firstName,
      registration.lastName,
      registration.email,
      registration.agencyAssociations,
      registration.agentNumbers
    );
  }

  public readonly key = 'SavingIndependentAgentRegistration';

  constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly email: ValidEmail,
    public readonly agencyAssociations: AgencyAssociation[],
    public readonly agentNumbers: ValidAgentNumber[]
  ) {}
}

export type IndependentAgentRegistration =
  | ValidIndependentAgentRegistration
  | SavingIndependentAgentRegistration
  | IndependentAgentRegistrationWithError
  | InvalidIndependentAgentRegistration
  | UnvalidatedIndependentAgentRegistration;

export type IndependentAgentRegistrationOptions<TReturn> = ToMatchOptions<IndependentAgentRegistration, 'key', TReturn>;

export const matchIndependentAgentRegistration = <TReturn>(
  independentAgentRegistration: IndependentAgentRegistration,
  options: IndependentAgentRegistrationOptions<TReturn>
): TReturn => {
  switch (independentAgentRegistration.key) {
    case 'SavingIndependentAgentRegistration':
      return options.SavingIndependentAgentRegistration(independentAgentRegistration);
    case 'IndependentAgentRegistrationWithError':
      return options.IndependentAgentRegistrationWithError(independentAgentRegistration);
    case 'InvalidIndependentAgentRegistration':
      return options.InvalidIndependentAgentRegistration(independentAgentRegistration);
    case 'UnvalidatedIndependentAgentRegistration':
      return options.UnvalidatedIndependentAgentRegistration(independentAgentRegistration);
    case 'ValidIndependentAgentRegistration':
      return options.ValidIndependentAgentRegistration(independentAgentRegistration);
  }
};
